import React, { useEffect, VFC } from 'react';
import { Routes, Route, Navigate, useNavigate, useMatch } from 'react-router-dom';
import { useMount, useUpdateEffect } from 'ahooks';
import { observer } from 'mobx-react';

import { useStores } from 'core';
import config from 'config';
import { PrivateRoute } from 'shared/components/domain/PrivateRoute';

import { Brands } from './Brands';
import { BrandView } from './BrandView';
import { Campaigns } from './Campaigns';
import { CampaignView } from './CampaignView';
import { CampaignEdit } from './CampaignEdit';
import { Accounts } from './Accounts';
import { Account } from './Account';
import { CabinetStatus } from './CabinetStatus';
import { Administration } from './Administration';
import { UploadStatistics } from './UploadStatistics';
import { PRGBenchmarks } from './PRGBenchmarks';
import { CompetitorAnalysis } from './CompetitorAnalysis';
import { Excess as Programmatic } from './Programmatic';
import { ReportConstructor, ReportList } from './reportConstructor';
import { AdFoxBanners, AdFoxCampaigns } from './tradingDesk';
import { CabinetCopying } from './CabinetCopying';
import { Hyundai } from './ClientsDashboards/Hyundai';
import { Auth } from './Auth';
import PageNotFound from './PageNotFound';
import { isTradingDeskUserOnly } from '../helpers/isTradingDeskUserOnly';

const pathnamesToAddAdvertiserId = ['brands', 'campaigns'].reduce<string[]>(
  (acc, x) => [...acc, `/${x}`, `/${x}/`],
  [],
);

function changeAdvertiserIdInPathname(navigate: ReturnType<typeof useNavigate>, id: number, pathname: string) {
  const newPathname = pathname.replace(/\/advertiser\/(\d+)\/\w+/, (match, foundId) =>
    foundId ? match.replace(foundId, id.toString()) : match,
  );
  navigate(newPathname);
}

function addAdvertiserIdToPathname(navigate: ReturnType<typeof useNavigate>, id: number, page: string) {
  navigate(`/advertiser/${id}/${page}`, { replace: true });
}

// Redirect wrapper components for v6 (Redirect 'from' prop no longer supported)
const AdvertiserBrandRedirect: VFC = () => {
  const match = useMatch('/advertiser/:advertiserId/brands/:id');
  const id = match?.params.id;
  return <Navigate to={`/brands/${id}`} replace />;
};

const AdvertiserCampaignRedirect: VFC = () => {
  const match = useMatch('/advertiser/:advertiserId/campaigns/:id');
  const id = match?.params.id;
  return <Navigate to={`/campaigns/${id}`} replace />;
};

const AppRoutes: VFC = () => {
  const navigate = useNavigate();
  const { pathname } = window.location;
  const { search } = window.location;
  const {
    authStore: { profile, isAuthorized },
  } = useStores();
  const match = useMatch('/advertiser/:advertiserId/:page');
  const advertiserId = Number.isNaN(Number(match?.params?.advertiserId)) ? null : Number(match?.params?.advertiserId);
  const {
    advertiserStore: { selectAdvertiser, currentAdvertiserId },
  } = useStores();

  useMount(() => {
    if (advertiserId && advertiserId > 0 && isAuthorized) {
      selectAdvertiser(advertiserId);
    } else if (typeof advertiserId === 'number' && advertiserId <= 0 && currentAdvertiserId) {
      changeAdvertiserIdInPathname(navigate, currentAdvertiserId, pathname);
    }
  });

  useEffect(() => {
    if (currentAdvertiserId && pathnamesToAddAdvertiserId.includes(pathname)) {
      const page = pathname.slice(1);
      addAdvertiserIdToPathname(navigate, currentAdvertiserId, page);
    }
  }, [pathname, currentAdvertiserId, navigate]);

  useUpdateEffect(() => {
    if (currentAdvertiserId && currentAdvertiserId !== advertiserId) {
      changeAdvertiserIdInPathname(navigate, currentAdvertiserId, pathname);
    }
  }, [currentAdvertiserId, navigate]);

  useUpdateEffect(() => {
    if (advertiserId && advertiserId !== currentAdvertiserId) {
      selectAdvertiser(advertiserId);
    }
  }, [advertiserId]);

  return (
    <Routes>
      <Route
        path="/brands"
        element={
          <PrivateRoute checkableFeature="brands">
            <Brands />
          </PrivateRoute>
        }
      />
      <Route
        path="/brands/:id/:year?"
        element={
          <PrivateRoute checkableFeature="brands">
            <BrandView />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <PrivateRoute checkableFeature="campaigns">
            <Campaigns />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/new"
        element={
          <PrivateRoute checkableFeature="editCampaign">
            <CampaignEdit />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <PrivateRoute checkableFeature="campaigns">
            <CampaignView />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/:id/edit"
        element={
          <PrivateRoute checkableFeature="editCampaign">
            <CampaignEdit />
          </PrivateRoute>
        }
      />
      <Route
        path="/advertiser/:advertiserId/brands"
        element={
          <PrivateRoute checkableFeature="brands">
            <Brands />
          </PrivateRoute>
        }
      />
      <Route
        path="/advertiser/:advertiserId/campaigns"
        element={
          <PrivateRoute checkableFeature="campaigns">
            <Campaigns />
          </PrivateRoute>
        }
      />
      <Route path="/advertiser/:advertiserId/brands/:id" element={<AdvertiserBrandRedirect />} />
      <Route path="/advertiser/:advertiserId/campaigns/:id" element={<AdvertiserCampaignRedirect />} />

      <Route
        path="/accounts"
        element={
          <PrivateRoute checkableFeature="accounts">
            <Accounts />
          </PrivateRoute>
        }
      />
      <Route
        path="/account/new"
        element={
          <PrivateRoute checkableFeature="account">
            <Account />
          </PrivateRoute>
        }
      />
      <Route
        path="/account/edit/:id/:cabinetId"
        element={
          <PrivateRoute checkableFeature="account">
            <Account />
          </PrivateRoute>
        }
      />

      <Route
        path="/cabinet-status"
        element={
          <PrivateRoute checkableFeature="cabinetStatus">
            <CabinetStatus />
          </PrivateRoute>
        }
      />

      <Route
        path="/administration/*"
        element={
          <PrivateRoute checkableFeature="administration">
            <Administration />
          </PrivateRoute>
        }
      />

      {config.isShowUploadStatistics && (
        <Route
          path="/upload-statistics"
          element={
            <PrivateRoute checkableFeature="uploadStatistics">
              <UploadStatistics />
            </PrivateRoute>
          }
        />
      )}

      <Route
        path="/prg-benchmarks"
        element={
          <PrivateRoute checkableFeature="prgBenchmarks">
            <PRGBenchmarks />
          </PrivateRoute>
        }
      />

      <Route
        path="/competitor-analysis"
        element={
          <PrivateRoute checkableFeature="competitorAnalysis">
            <CompetitorAnalysis />
          </PrivateRoute>
        }
      />

      <Route
        path="/programmatic"
        element={
          <PrivateRoute checkableFeature="programmatic">
            <Programmatic />
          </PrivateRoute>
        }
      />

      <Route
        path="/report-constructor"
        element={
          <PrivateRoute checkableFeature="reportConstructor">
            <ReportList />
          </PrivateRoute>
        }
      />
      <Route
        path="/report-constructor/new"
        element={
          <PrivateRoute checkableFeature="reportConstructor">
            <ReportConstructor />
          </PrivateRoute>
        }
      />
      <Route
        path="/report-constructor/:id/:mode"
        element={
          <PrivateRoute checkableFeature="reportConstructor">
            <ReportConstructor />
          </PrivateRoute>
        }
      />

      <Route
        path="/trading-desk/adfox"
        element={
          <PrivateRoute checkableFeature="tradingDesk">
            <AdFoxCampaigns />
          </PrivateRoute>
        }
      />

      <Route
        path="/trading-desk/adfox/:campaignId/banners"
        element={
          <PrivateRoute checkableFeature="tradingDesk">
            <AdFoxBanners />
          </PrivateRoute>
        }
      />

      <Route
        path="/cabinet-copying"
        element={
          <PrivateRoute checkableFeature="cabinetCopying">
            <CabinetCopying />
          </PrivateRoute>
        }
      />

      <Route path="/auth" element={<Auth />} />
      <Route
        path="/app"
        element={<Navigate to={isTradingDeskUserOnly(profile) ? '/trading-desk/adfox' : '/brands'} replace />}
      />
      <Route path="/dashboard/hyundai" element={<Hyundai />} />

      <Route path="/" element={<Navigate to={{ pathname: '/auth', search }} replace />} />

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default observer(AppRoutes);
